import produce from 'immer';
import { Action } from 'redux';
import { RootState } from '.';
import { ResitSubject } from '../api/candidate/resit/model';
import { CoordinatorCandidateResitRegistration } from '../models/@candidate/candidateResitRegistrationSchema';

// =============================================================================
// State
// =============================================================================
interface ResitState {
  candidateSelectedSubjects: ResitSubject[];
  candidateResitList: CoordinatorCandidateResitRegistration;
  bankTransferEnabled: boolean;
  applicationUuid: string;
}

export const INITIAL_RESIT_STATE: ResitState = {
  candidateSelectedSubjects: [],
  candidateResitList: { candidateDetails: [], examLevel: undefined },
  bankTransferEnabled: false,
  applicationUuid: '',
};

// =============================================================================
// Action Type
// =============================================================================
enum ResitActionTypes {
  UPDATE_RESIT_SELECTED_SUBJECTS = 'resit/update_resit_selected_subjects',
  UPDATE_RESIT_CANDIDATE_REGISTER = 'resit/update_resit_candidate_register',
  UPDATE_RESIT_BANK_TRANSFER_OPTION = 'resit/update_resit_bank_transfer_option',
  UPDATE_RESIT_APPLICATION_UUID = 'resit/update_resit_application_id_option',
}

// =============================================================================
// Actions
// =============================================================================
interface CandidateSelectedSubjectsAction
  extends Action<ResitActionTypes.UPDATE_RESIT_SELECTED_SUBJECTS> {
  payload: ResitSubject[];
}

interface CandidateResitRegisterAction
  extends Action<ResitActionTypes.UPDATE_RESIT_CANDIDATE_REGISTER> {
  payLoad: CoordinatorCandidateResitRegistration;
}

interface UpdateBankTransferOptionAction
  extends Action<ResitActionTypes.UPDATE_RESIT_BANK_TRANSFER_OPTION> {
  payload: boolean;
}

interface UpdateApplicationUuidAction
  extends Action<ResitActionTypes.UPDATE_RESIT_APPLICATION_UUID> {
  payload: string;
}

// =============================================================================
// Action Creators
// =============================================================================
export const UpdateResitCandidateSelectedSubjects = (
  selectedSubjects: ResitSubject[]
): CandidateSelectedSubjectsAction => ({
  type: ResitActionTypes.UPDATE_RESIT_SELECTED_SUBJECTS,
  payload: selectedSubjects,
});

export const UpdateCandidateResitRegistration = (
  candidateRegistration: CoordinatorCandidateResitRegistration
): CandidateResitRegisterAction => ({
  type: ResitActionTypes.UPDATE_RESIT_CANDIDATE_REGISTER,
  payLoad: candidateRegistration,
});

export function UpdateBankTransferOption(
  bankTransferOption: boolean
): UpdateBankTransferOptionAction {
  return {
    type: ResitActionTypes.UPDATE_RESIT_BANK_TRANSFER_OPTION,
    payload: bankTransferOption,
  };
}

export function UpdateApplicationUuid(applicationUuid: string): UpdateApplicationUuidAction {
  return {
    type: ResitActionTypes.UPDATE_RESIT_APPLICATION_UUID,
    payload: applicationUuid,
  };
}

type ResitActions =
  | CandidateSelectedSubjectsAction
  | CandidateResitRegisterAction
  | UpdateBankTransferOptionAction
  | UpdateApplicationUuidAction;

// =============================================================================
// Reducer
// =============================================================================
export const resitReducers = produce((draft: ResitState, action: ResitActions) => {
  switch (action.type) {
    case ResitActionTypes.UPDATE_RESIT_SELECTED_SUBJECTS: {
      draft.candidateSelectedSubjects = action.payload;
      break;
    }
    case ResitActionTypes.UPDATE_RESIT_CANDIDATE_REGISTER: {
      draft.candidateResitList = action.payLoad;
      break;
    }
    case ResitActionTypes.UPDATE_RESIT_BANK_TRANSFER_OPTION: {
      draft.bankTransferEnabled = action.payload;
      break;
    }
    case ResitActionTypes.UPDATE_RESIT_APPLICATION_UUID: {
      draft.applicationUuid = action.payload;
      break;
    }
    default:
  }
}, INITIAL_RESIT_STATE);

// =============================================================================
// Selectors
// =============================================================================
export const selectCandidateSelectedSubjects = (state: RootState) =>
  state.resit.candidateSelectedSubjects;
export const selectCandidateResitRegistration = (state: RootState) =>
  state.resit.candidateResitList;
export const selectBankTransferOption = (state: RootState) => state.resit.bankTransferEnabled;
export const selectApplicationUuid = (state: RootState) => state.resit.applicationUuid;
